import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LookupSearchField from './tablemaint/LookupSearchField';
import LookupResultCard from '../pages/order-management/components/LookupResultCard';
import quoteService from '../services/quote.service';
import type { CustomerLookupRow } from '../pages/order-management/fixtures';

/**
 * Reusable enriched Customer picker — the customer twin of BillToLookupField.
 * Search cms.customers (name / code) and store the customercode, showing the
 * name. Built on the shared <LookupSearchField> + enriched <LookupResultCard>
 * (address + Sales Rep / Market / Key Acct) so it matches the Bill To field's
 * look exactly (same input size, label, and status line). Lookup-only.
 */
export interface CustomerValue {
  code: number; // 0 = unset
  name: string;
}

const EMPTY: CustomerValue = { code: 0, name: '' };

interface Props {
  value: CustomerValue;
  onChange: (v: CustomerValue) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
}

export const CustomerLookupField: React.FC<Props> = ({ value, onChange, label = 'Customer', disabled, required }) => (
  <div>
    <LookupSearchField<CustomerLookupRow>
      label={label}
      required={required}
      displayValue={value.name}
      onType={(text) => onChange({ code: 0, name: text })}
      onSelect={(r) => onChange({ code: r.customercode, name: r.custname })}
      search={(q) => quoteService.lookupCustomer(q)}
      renderItem={(r) => (
        <LookupResultCard
          name={r.custname}
          code={r.customercode}
          address1={r.address1}
          address2={r.address2}
          city={r.city}
          state={r.state}
          zip={r.zip}
          details={[
            { label: 'Sales Rep', value: r.salesrep },
            { label: 'Market', value: r.market },
            { label: 'Key Acct', value: r.keyAccount },
          ]}
        />
      )}
      itemValue={(r) => String(r.customercode)}
      itemLabel={(r) => r.custname}
      disabled={disabled}
      placeholder="Search Customers by name or #…"
    />
    <div className="mt-1.5 flex items-center gap-3 text-xs">
      {value.code > 0 ? (
        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
          #{value.code}
          {!disabled && (
            <button type="button" onClick={() => onChange(EMPTY)} title="Clear"
              className="ml-1 text-gray-400 hover:text-red-500">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ) : (
        <span className="text-muted">No Customer selected</span>
      )}
    </div>
  </div>
);

export default CustomerLookupField;
